import React from "react";
import { toCommaSeperatedAmount,dateFormate, } from "./../Utils/util";
import Image from "./Base/Image";
/* ─────────────────────────────────────────────────────
   BREAKPOINTS
   < 540px   → 1 col  (mobile)   — only pinned fields shown in header/footer
   540–859px → 2 cols (tablet)   — body fields visible
   860–1099px→ 3 cols (desktop)  — body fields visible
   ≥ 1100px  → 4 cols (wide)     — body fields visible
───────────────────────────────────────────────────── */
function useScreenSize(maxDesktopColumns = 4) {
  const getSize = () => {
    const w = window.innerWidth;
    if (w >= 1100) return maxDesktopColumns;
    if (w >= 860)  return 3;
    if (w >= 540)  return 2;
    return 1;
  };
  const [cols, setCols] = React.useState(getSize);
  React.useEffect(() => {
    const handler = () => setCols(getSize());
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return cols; // number of grid columns
}

/* ─────────────────────────────────────────────────────
   STYLES
───────────────────────────────────────────────────── */
const S = {
  wrap: {
    display: "flex", flexDirection: "column", gap: "10px",
    padding: "10px 10px 60px",
  },
  topBar: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    paddingLeft: "4px", marginBottom: "2px",
  },
  count: {
    fontSize: "11px", fontWeight: 600, color: "#94a3b8",
    textTransform: "uppercase", letterSpacing: "0.07em",
  },
  colLabel: (dark) => ({
    fontSize: "10px", fontWeight: 500,
    color: dark ? "#555f78" : "#94a3b8",
    letterSpacing: "0.05em",
    border: `1px solid ${dark ? "#272b3a" : "#e4eaf5"}`,
    borderRadius: "99px", padding: "2px 8px",
  }),

/* ── Summary strip (subheader totals) ── */
  summaryWrap: {
    display: "flex", flexWrap: "wrap", gap: "8px",
    padding: "0 4px 2px",
  },
  summaryChip: (dark) => ({
    display: "flex", flexDirection: "column", gap: "3px",
    background: dark ? "#1a1d27" : "#ffffff",
    border: `1px solid ${dark ? "#272b3a" : "#e8edf5"}`,
    borderRadius: "10px",
    padding: "8px 12px",
    minWidth: "100px",
    flex: "1 1 100px",
    boxShadow: dark
      ? "0 2px 10px rgba(0,0,0,0.3)"
      : "0 1px 3px rgba(0,0,0,0.05)",
  }),
  summaryLabel: (dark) => ({
    fontSize: "9px", fontWeight: 700,
    color: dark ? "#555f78" : "#94a3b8",
    textTransform: "uppercase", letterSpacing: "0.06em",
    whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
  }),
  summaryValue: (dark) => ({
    fontSize: "13px", fontWeight: 700,
    color: dark ? "#60a5fa" : "#2563eb",
    fontVariantNumeric: "tabular-nums",
    whiteSpace: "nowrap",
  }),
  /* grid: columns injected at render time via gridTemplateColumns */
  grid: (numCols) => ({
    display: "grid",
    gridTemplateColumns: `repeat(${numCols}, minmax(0, 1fr))`,
    gap: "10px",
  }),

  card: (dark) => ({
    background: dark ? "#1a1d27" : "#ffffff",
    border: `1px solid ${dark ? "#272b3a" : "#e8edf5"}`,
    borderRadius: "14px",
    boxShadow: dark
      ? "0 2px 12px rgba(0,0,0,0.35)"
      : "0 1px 4px rgba(0,0,0,0.06), 0 4px 16px rgba(0,0,0,0.04)",
    overflow: "hidden",
    fontFamily: '"Montserrat", Helvetica, Arial, sans-serif',
    display: "flex", flexDirection: "column",
  }),
  cardHeader: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    padding: "12px 14px 10px", gap: "10px", userSelect: "none",
  },
  headerLeft: {
    display: "flex", alignItems: "center", gap: "10px", minWidth: 0, flex: 1,
  },
  headerRight: {
    display: "flex", alignItems: "center", gap: "6px", flexShrink: 0,
  },
  avatar: (dark) => ({
    width: "36px", height: "36px", minWidth: "36px",
    borderRadius: "10px",
    background: dark ? "#0d1f3c" : "#dbeafe",
    color: dark ? "#60a5fa" : "#1d4ed8",
    border: `1.5px solid ${dark ? "#60a5fa" : "#3b82f6"}`,
    fontSize: "11px", fontWeight: 700, letterSpacing: "0.03em",
    display: "flex", alignItems: "center", justifyContent: "center",
    textTransform: "uppercase", flexShrink: 0,
  }),
  titles: {
    minWidth: 0, flex: 1, display: "flex", flexDirection: "column", gap: "2px",
  },
  titleCode: (dark) => ({
    fontSize: "13px", fontWeight: 700,
    color: dark ? "#60a5fa" : "#2563eb",
    whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", lineHeight: 1.2,
  }),
  titleSub: (dark) => ({
    fontSize: "11px", color: dark ? "#8b93a8" : "#64748b", fontWeight: 400,
    whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", lineHeight: 1.3,
  }),
  badge: (dark) => ({
    display: "inline-flex", alignItems: "center",
    padding: "3px 10px", borderRadius: "99px",
    fontSize: "10px", fontWeight: 600,
    border: `1.5px solid ${dark ? "#272b3a" : "#e2e8f0"}`,
    background: dark ? "#1e2130" : "#f1f5f9",
    color: dark ? "#8b93a8" : "#64748b",
    letterSpacing: "0.03em", whiteSpace: "nowrap", lineHeight: 1.4,
  }),
  footer: (dark) => ({
    display: "flex", alignItems: "center", justifyContent: "space-between",
    padding: "7px 14px 10px",
    borderTop: `1px solid ${dark ? "#272b3a" : "#e8edf5"}`,
    gap: "8px", marginTop: "auto",
  }),
  dateChip: (dark) => ({
    display: "inline-flex", alignItems: "center", gap: "5px",
    fontSize: "11px", fontWeight: 500,
    color: dark ? "#8b93a8" : "#64748b",
    background: dark ? "#1e2130" : "#f1f5fb",
    border: `1px solid ${dark ? "#272b3a" : "#e4eaf5"}`,
    borderRadius: "99px", padding: "3px 10px",
  }),
  dateDot: {
    width: "6px", height: "6px", borderRadius: "50%",
    background: "#3b82f6", opacity: 0.8, flexShrink: 0,
  },
actionBtn: (dark) => ({
    width: "30px", height: "30px", borderRadius: "8px",
    border: `1px solid ${dark ? "#272b3a" : "#e8edf5"}`,
    background: dark ? "#1e2130" : "#f7f9fd",
    color: dark ? "#8b93a8" : "#f00909",
    display: "flex", alignItems: "center", justifyContent: "center",
    cursor: "pointer", fontSize: "10px", fontWeight: 700,
    padding: 0, outline: "none", flexShrink: 0,
    fontFamily: '"Montserrat", Helvetica, Arial, sans-serif',
  }),
  body: (dark) => ({
    borderTop: `1px solid ${dark ? "#272b3a" : "#e8edf5"}`,
    background: dark ? "#1e2130" : "#f7f9fd",
    padding: "8px 14px 4px",
    flex: 1,
  }),
  detailRow: (dark, last) => ({
    display: "grid",
    gridTemplateColumns: "130px 1fr",
    alignItems: "center",
    padding: "5px 0",
    borderBottom: last ? "none" : `1px solid ${dark ? "#272b3a" : "#e4eaf5"}`,
  }),
  detailLabel: (dark) => ({
    fontSize: "10px",
    fontWeight: 600,
    color: dark ? "#555f78" : "#94a3b8",
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    whiteSpace: "nowrap",
  }),
  detailValue: (dark) => ({
    fontSize: "12px",
    fontWeight: 500,
    color: dark ? "#e8ecf5" : "#1a1d23",
    textAlign: "left",
    wordBreak: "break-word",
  }),
  detailNumericBadge: (dark) => ({
    display: "inline-block",
    justifySelf: "start",
    background: dark ? "#0d1f3c" : "#eff6ff",
    color: dark ? "#60a5fa" : "#2563eb",
    // border: `1px solid ${dark ? "#1e3a5f" : "#bfdbfe"}`,
    // borderRadius: "6px",
    padding: "2px 10px",
    fontSize: "12px",
    fontWeight: 700,
    fontVariantNumeric: "tabular-nums",
    letterSpacing: "0.01em",
    lineHeight: "1.7",
    whiteSpace: "nowrap",
  }),
  hyperLink: {
    color: "#3b82f6", cursor: "pointer", textDecoration: "underline",
  },
   imgWrap: (dark) => ({
    width: "44px", height: "44px", borderRadius: "8px",
    overflow: "hidden", justifySelf: "start",
    background: dark ? "#1e2130" : "#f1f5f9",
    display: "flex", alignItems: "center", justifyContent: "center",
  }),
  empty: {
    display: "flex", flexDirection: "column", alignItems: "center",
    justifyContent: "center", padding: "3rem 1.5rem", gap: "10px",
    fontFamily: '"Montserrat", Helvetica, Arial, sans-serif',
  },
  emptyText: (dark) => ({ fontSize: "13px", fontWeight: 600, color: dark ? "#8b93a8" : "#64748b", margin: 0 }),
  emptySub:  (dark) => ({ fontSize: "12px", color: dark ? "#555f78" : "#94a3b8", margin: 0 }),
  skeletonCard: (dark) => ({
    background: dark ? "#1a1d27" : "#ffffff",
    border: `1px solid ${dark ? "#272b3a" : "#e8edf5"}`,
    borderRadius: "14px", padding: "12px 14px",
    display: "flex", flexDirection: "column", gap: "10px",
  }),
};

/* ── Dark mode watcher ── */
function useDarkMode() {
  const [dark, setDark] = React.useState(() => document.body.classList.contains("dark"));
  React.useEffect(() => {
    const obs = new MutationObserver(() =>
      setDark(document.body.classList.contains("dark"))
    );
    obs.observe(document.body, { attributes: true, attributeFilter: ["class"] });
    return () => obs.disconnect();
  }, []);
  return dark;
}

/* ── Shimmer ── */
const shimmerCSS = `@keyframes _mcbShimmer{0%{background-position:200% 0}100%{background-position:-200% 0}}
.mcb-img-wrap img {
  width: 100% !important;
  height: 100% !important;
  object-fit: contain !important;
  object-position: center !important;
}
`;
function useShimmer() {
  React.useEffect(() => {
    const id = "_mcbShimmerStyle";
    if (document.getElementById(id)) return;
    const s = document.createElement("style");
    s.id = id;
    s.textContent = shimmerCSS;
    document.head.appendChild(s);
    return () => { const el = document.getElementById(id); if (el) el.remove(); };
  }, []);
}

const Shimmer = ({ w, h, r, dark }) => {
  useShimmer();
  return (
    <div style={{
      width: w, height: h, borderRadius: r || 4, flexShrink: 0,
      background: dark
        ? "linear-gradient(90deg,#1e2130 25%,#252840 50%,#1e2130 75%)"
        : "linear-gradient(90deg,#f1f5fb 25%,#e4eaf5 50%,#f1f5fb 75%)",
      backgroundSize: "200% 100%",
      animation: "_mcbShimmer 1.4s infinite",
    }} />
  );
};

/* ── Skeleton ── */
const SkeletonCard = ({ dark }) => (
  <div style={S.skeletonCard(dark)}>
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      <Shimmer w={36} h={36} r={10} dark={dark} />
      <div style={{ display: "flex", flexDirection: "column", gap: 6, flex: 1 }}>
        <Shimmer w="55%" h={13} dark={dark} />
        <Shimmer w="35%" h={10} dark={dark} />
      </div>
      <Shimmer w={60} h={22} r={99} dark={dark} />
    </div>
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
      <Shimmer w={90} h={20} r={99} dark={dark} />
      <div style={{ display: "flex", gap: 6 }}>
        <Shimmer w={30} h={30} r={8} dark={dark} />
        <Shimmer w={30} h={30} r={8} dark={dark} />
      </div>
    </div>
  </div>
);

/* ── Empty state ── */
const EmptyState = ({ text, dark }) => (
  <div style={S.empty}>
    <svg width="52" height="52" viewBox="0 0 52 52" fill="none">
      <rect x="7" y="11" width="38" height="30" rx="5"
        stroke={dark ? "#3a3f52" : "#e2e8f0"} strokeWidth="2" fill="none"/>
      <line x1="14" y1="20" x2="38" y2="20" stroke={dark ? "#3a3f52" : "#e2e8f0"} strokeWidth="1.5" strokeLinecap="round"/>
      <line x1="14" y1="27" x2="30" y2="27" stroke={dark ? "#3a3f52" : "#e2e8f0"} strokeWidth="1.5" strokeLinecap="round"/>
      <line x1="14" y1="34" x2="22" y2="34" stroke={dark ? "#3a3f52" : "#e2e8f0"} strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
    <p style={S.emptyText(dark)}>{text || "No data available"}</p>
    <p style={S.emptySub(dark)}>You're fully up to date here.</p>
  </div>
);

/* ── Render cell value ── */
function renderValue(col, row) {
  const raw = row[col.accessor];
   if (col.isImage      && col.getImage)          return col.getImage(col, row, raw)          ?? "—";
  if (col.isValueConverter && col.getValueConverter) return col.getValueConverter(col, row, raw) ?? "—";
  if (col.isValueConveter && col.getValueConveter) return col.getValueConveter(col, row, raw) ?? "—";
  if (col.isGraph      && col.getGraph)           return col.getGraph(col, row, raw)           ?? "—";
  if (col.isStatus     && col.getStatusCaption)   return col.getStatusCaption(raw)             ?? "—";
  if (col.isNumeric)   return toCommaSeperatedAmount(raw);
  if (col.isSpecial)   return `${raw} - ${row[col.specialAccessor]}`;
   if (col.isDateFormate)   return dateFormate(raw);
  return raw ?? "—";
}

/* ── Render a subheader/summary value (same shape as renderValue, but
   reads off subHeaderData using the column's subHeaderAccessor instead
   of off a row using col.accessor) ── */
function renderSummaryValue(col, subHeaderData) {
  const raw = subHeaderData ? subHeaderData[col.subHeaderAccessor] : null;
  if (col.isNumeric) return toCommaSeperatedAmount(raw);
  return raw ?? "—";
}

/* ══════════════════════════════════════════════
   SUMMARY STRIP — mirrors CustomTable's subHeaderData row.
   Renders one chip per column flagged with setSubHeaderDetails(true, key).
══════════════════════════════════════════════ */
const SummaryStrip = ({ columns, subHeaderData, dark }) => {
  const summaryCols = columns.filter((c) => c.isSubHeader && c.subHeaderAccessor);
  if (!summaryCols.length || !subHeaderData) return null;

  return (
    <div style={S.summaryWrap}>
      {summaryCols.map((col, i) => (
        <div key={col.subHeaderAccessor || i} style={S.summaryChip(dark)}>
          <span style={S.summaryLabel(dark)}>{col.header}</span>
          <span style={S.summaryValue(dark)}>{renderSummaryValue(col, subHeaderData)}</span>
        </div>
      ))}
    </div>
  );
};
/* ══════════════════════════════════════════════
   SINGLE CARD
══════════════════════════════════════════════ */
const MobileCard = ({
  row,
  columns,
  codeAccessor,
  subAccessor,
  dateAccessor,
  statusAccessor,
  dark,
  numCols,    
  graphAccessor
      // ← grid column count (1 / 2 / 3 / 4)
}) => {

  const buttonCols = columns.filter((c) => c.isButton);

  const statusCol = statusAccessor
    ? columns.find((c) => c.isStatus && c.accessor === statusAccessor)
    : null;

     const graphCol = graphAccessor
    ? columns.find((c) => c.isGraph && c.accessor === graphAccessor)
    : null;


  const pinned = [codeAccessor, subAccessor, dateAccessor, statusAccessor].filter(Boolean);

  const allBodyCols = columns.filter(
    (c) => !c.isButton && !c.isTopHeader && c.header && !pinned.includes(c.accessor)
  );

  /*
    On mobile (1 col) → show ALL body fields (same as before).
    On ≥ 2 cols       → also show ALL body fields; the narrower card
                        naturally fits more vertical info side-by-side.
    You can slice/limit here if preferred, e.g. allBodyCols.slice(0, numCols > 1 ? 8 : 4)
  */
  const bodyCols = allBodyCols;

  const code   = codeAccessor   ? row[codeAccessor]   : null;
  const sub    = subAccessor    ? row[subAccessor]    : null;
  const date   = dateAccessor   ? row[dateAccessor]   : null;
  const status = statusAccessor ? row[statusAccessor] : null;
  const graph = graphAccessor ? row[graphAccessor] : null;

  const statusLabel = statusCol?.getStatusCaption
    ? statusCol.getStatusCaption(status)
    : status != null ? String(status) : null;

   const graphClass = graphCol?.getGraph
    ? graphCol.getGraph(graphCol, row, graph)
    : null;

  const initials = code
    ? (String(code).replace(/\D/g, "").slice(-2) || String(code).slice(0, 2))
    : null;

  return (
    <div style={S.card(dark)}>

      {/* ── Header ── */}
      <div style={S.cardHeader}>
        <div style={S.headerLeft}>
          {initials != null && <div style={S.avatar(dark)}>{initials}</div>}
          <div style={S.titles}>
            {code != null && <div style={S.titleCode(dark)}>{code}</div>}
            {sub  != null && <div style={S.titleSub(dark)}>{sub}</div>}
          </div>
        </div>
        {statusLabel != null && (
          <div style={S.headerRight}>
            <span style={S.badge(dark)}>{statusLabel}</span>
          </div>
        )}
      </div>

      {/* ── Body ── */}
      {bodyCols.length > 0 && (
        <div style={S.body(dark)}>
          {bodyCols.map((col, i) => {
            const isLast = i === bodyCols.length - 1;
            const isGraphCell = !!graphAccessor && col.accessor === graphAccessor && !!graphClass;
            const isImageCell = col.isImage && col.getImage;
            const isRight = (col.alignment === "right" || col.isNumeric || isGraphCell);
 if (col.isImage) {
              return (
                <div key={col.accessor || i} style={S.detailRow(dark, isLast)}>
                  <span style={S.detailLabel(dark)}>{col.header}</span>
                  <div className="mcb-img-wrap" style={S.imgWrap(dark)}>
                    <Image
                      url={`${process.env.REACT_APP_API_URL}/${process.env.REACT_APP_IMAGE_PATH}${row[col.accessor]}`}
                      altName={`${row[col.accessor]} - image`}
                    />
                  </div>
                </div>
              );
            }
            return (
              <div key={col.accessor || i} style={S.detailRow(dark, isLast)}>
                <span style={S.detailLabel(dark)}>{col.header}</span>

                {/* {isImageCell ? (
                  <div className="spo-style-card__img-wrap" >
                    {renderValue(col, row)}
                  </div>
                ) : */}
                 {isGraphCell ? (
                  <span style={S.detailNumericBadge(dark)}>
                    <div className={graphClass}></div>
                  </span>
                ) : isRight ? (
                  <span style={S.detailNumericBadge(dark)}>
                    {col.isHyperLink
                      ? <span style={S.hyperLink} onClick={(e) => col.clickHandler && col.clickHandler(row[col.accessor], e, row)}>{renderValue(col, row)}</span>
                      : renderValue(col, row)
                    }
                  </span>
                ) : (
                  <span style={S.detailValue(dark)}>
                    {col.isHyperLink
                      ? <span style={S.hyperLink} onClick={(e) => col.clickHandler && col.clickHandler(row[col.accessor], e, row)}>{renderValue(col, row)}</span>
                      : renderValue(col, row)
                    }
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── Footer ── */}
      <div style={S.footer(dark)}>
        {date != null
          ? <span style={S.dateChip(dark)}><span style={S.dateDot} />{date}</span>
          : <span />
        }
        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          {buttonCols.map((col, i) => (
            <button
              key={col.accessor || i}
              style={S.actionBtn(dark)}
              title={col.header || ""}
              disabled={col.disable}
              onClick={(e) => {
                e.stopPropagation();
                if (col.clickHandler) col.clickHandler(row[col.accessor], e, row);
              }}
            >
              {col.buttonRender}
            </button>
          ))}
        </div>
      </div>

    </div>
  );
};

/* ══════════════════════════════════════════════
   MAIN EXPORT
══════════════════════════════════════════════ */
/**
 * MobileCardTable — responsive card grid.
 *
 * Screen width   → grid columns
 * < 540px        → 1 col  (mobile)
 * 540 – 859px    → 2 cols (tablet)
 * 860 – 1099px   → 3 cols (desktop)
 * ≥ 1100px       → 4 cols (wide)
 *
 * Props (same as before — no breaking changes)
 * ─────
 * data           array   row data
 * columns        array   ColumnDefinition-built array
 * codeAccessor   string  bold title field          e.g. "code"
 * subAccessor    string  subtitle field            e.g. "customerName"
 * dateAccessor   string  date chip field           e.g. "createdDate"
 * statusAccessor string  status badge field        e.g. "orderStatus"
 * rowIdAccessor  string  unique row key field      e.g. "id"
* subHeaderData  object  same object passed to CustomTable's subHeaderData —
 *                        renders a chip per column flagged with
 *                        setSubHeaderDetails(true, key) above the card grid.
 * emptyText      string  custom empty message
 * loading        bool    show skeleton cards
 */
const MobileCardTable = ({
  data = [],
  columns = [],
  codeAccessor,
  subAccessor,
  dateAccessor,
  statusAccessor,
  rowIdAccessor,
 subHeaderData,
  emptyText,
  loading = false,
  graphAccessor,
  maxDesktopColumns,
  className = ""
}) => {
  const dark    = useDarkMode();
  const numCols = useScreenSize(maxDesktopColumns); // 1 | 2 | 3 | 4

  const colLabel = {
    1: "1 col",
    2: "2 cols",
    3: "3 cols",
    4: "4 cols",
  }[numCols];

  if (loading) return (
    <div style={S.wrap}>
      <div style={S.grid(numCols)}>
        {[1, 2, 3, 4].slice(0, numCols).map(i => <SkeletonCard key={i} dark={dark} />)}
      </div>
    </div>
  );

  if (!data.length) return <EmptyState text={emptyText} dark={dark} />;

  return (
    <div className={className} style={S.wrap}>

      {/* ── Top bar: record count + column indicator ── */}
      <div style={S.topBar}>
        <span style={S.count}>{data.length} records</span>
        <span style={S.colLabel(dark)}>{colLabel}</span>
      </div>

 {/* ── Summary strip: totals from subHeaderData, same as CustomTable ── */}
      <SummaryStrip columns={columns} subHeaderData={subHeaderData} dark={dark} />
      {/* ── Responsive grid ── */}
      <div style={S.grid(numCols)}>
        {data.map((row, idx) => (
          <MobileCard
            key={rowIdAccessor ? row[rowIdAccessor] : idx}
            row={row}
            columns={columns}
            codeAccessor={codeAccessor}
            subAccessor={subAccessor}
            dateAccessor={dateAccessor}
            statusAccessor={statusAccessor}
            //statusAccessor
            dark={dark}
            numCols={numCols}
            graphAccessor={graphAccessor}
          />
        ))}
      </div>

    </div>
  );
};

export default MobileCardTable;